import {
  Button,
  createTableColumn,
  DrawerBody,
  DrawerHeader,
  DrawerHeaderTitle,
  OverlayDrawer,
  type TableColumnDefinition,
  Text,
} from "@fluentui/react-components"
import { Dismiss24Regular, PeopleListRegular } from "@fluentui/react-icons"
import type { Node, ShardAssignment } from "@rema/shared/schemas/nodes"
import { DataGridPage } from "../../components/data-grid-page"
import { EmptyState } from "../../components/empty-state"
import { es } from "../../i18n/es"
import { nodesApi } from "./api"
import { nodesI18n } from "./i18n"

export interface AssignmentsDrawerProps {
  // El nodo cuyas asignaciones se muestran; null = cerrado
  node: Node | null
  onClose: () => void
}

const formatDate = (iso: string | null): string =>
  iso ? new Date(iso).toLocaleString() : nodesI18n.none

const columns: TableColumnDefinition<ShardAssignment>[] = [
  createTableColumn<ShardAssignment>({
    columnId: "user",
    renderHeaderCell: () => nodesI18n.assignments.columns.user,
    renderCell: (item) => <Text font="monospace">{item.user_id}</Text>,
  }),
  createTableColumn<ShardAssignment>({
    columnId: "strategy",
    renderHeaderCell: () => nodesI18n.assignments.columns.strategy,
    renderCell: (item) => nodesI18n.strategy[item.strategy],
  }),
  createTableColumn<ShardAssignment>({
    columnId: "assignedAt",
    renderHeaderCell: () => nodesI18n.assignments.columns.assignedAt,
    renderCell: (item) => formatDate(item.assigned_at),
  }),
  createTableColumn<ShardAssignment>({
    columnId: "previousNode",
    renderHeaderCell: () => nodesI18n.assignments.columns.previousNode,
    renderCell: (item) => item.previous_node_id ?? nodesI18n.none,
  }),
  createTableColumn<ShardAssignment>({
    columnId: "reassignedAt",
    renderHeaderCell: () => nodesI18n.assignments.columns.reassignedAt,
    renderCell: (item) => formatDate(item.reassigned_at),
  }),
]

// Panel lateral con las asignaciones usuario→nodo de un nodo (cursor). Reusa el
// DataGridPage del kit (paginación, estados de carga/error/vacío consistentes).
export const AssignmentsDrawer = ({ node, onClose }: AssignmentsDrawerProps) => (
  <OverlayDrawer
    position="end"
    size="medium"
    open={node !== null}
    onOpenChange={(_, data) => {
      if (!data.open) onClose()
    }}
  >
    <DrawerHeader>
      <DrawerHeaderTitle
        action={
          <Button
            appearance="subtle"
            aria-label={es.common.close}
            icon={<Dismiss24Regular />}
            onClick={onClose}
          />
        }
      >
        {node ? `${nodesI18n.assignments.title} · ${node.name}` : nodesI18n.assignments.title}
      </DrawerHeaderTitle>
    </DrawerHeader>
    <DrawerBody>
      {node ? (
        <DataGridPage<ShardAssignment>
          // La clave incluye el id del nodo para aislar el cache por nodo.
          queryKey={["shard-assignments", node.id]}
          fetchPage={(cursor) => nodesApi.listAssignments({ node_id: node.id }, cursor)}
          columns={columns}
          getRowId={(item) => item.user_id}
          emptyState={
            <EmptyState
              icon={<PeopleListRegular />}
              title={nodesI18n.assignments.empty}
              description={nodesI18n.assignments.emptyDescription}
            />
          }
        />
      ) : null}
    </DrawerBody>
  </OverlayDrawer>
)
